import React, {useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {ADataTable, APagination} from "@cisco-sbg-ui/magna-react";
import classNames from "classnames";
import {useTranslation} from "react-i18next";
import SeverityChip from "features/incidents/IncidentsTable/SeverityChip";
import {
  AssetsList,
  IndicatorsList,
  ObservablesList
} from "components/EntityList";
import TableEmptyState from "components/Table/TableEmptyState";
import {FieldNames} from "models/fields";
import {EVENTS_SORT_BY_MAP, SORT_ORDER_MAP} from "models/options";
import {
  paginateTableData,
  sortByNestedTime,
  sortBySeverity,
  tableLoadingState
} from "utils/dataTable";
import {ENTITY_TYPE} from "utils/entities";
import TooltipDelay from "../../components/TooltipDelay";
import FirstSeenCol from "./columnRenderers/FirstSeenCol";
import SourceCol from "./columnRenderers/SourceCol";
import ImportantEventIcon from "./components/ImportantEventIcon";
import css from "./EventsTable.module.scss";

const EventsTable = ({
  eventTotalCount,
  filterCount,
  isLoading,
  openDetectionDrawer,
  tableData,
  openEntityDrawer
}) => {
  const {t} = useTranslation();
  const [sort, setSort] = useState({
    key: FieldNames.observedTime,
    direction: SORT_ORDER_MAP.desc.value
  });
  const [eventsPerPage, setEventsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const hasZeroEvents = eventTotalCount === 0;

  const isEmpty = !isLoading && hasZeroEvents;

  const hasFiltersApplied = filterCount > 0;

  const sortedData = useMemo(() => {
    if (!tableData.length) return [];

    if (sort) {
      const {key, direction} = sort;
      switch (key) {
        case EVENTS_SORT_BY_MAP.ObservedTime.value:
          return sortByNestedTime(
            tableData,
            direction,
            FieldNames.observedTime,
            FieldNames.startTime
          );
        case EVENTS_SORT_BY_MAP.Severity.value:
          return sortBySeverity(tableData, direction);
        default:
          return tableData;
      }
    }
    return tableData;
  }, [tableData, sort]);

  const paginatedEvents = useMemo(
    () => paginateTableData(sortedData, currentPage, eventsPerPage),
    [sortedData, currentPage, eventsPerPage]
  );

  useEffect(() => {
    // If we know eventTotalCount is not 0, but currentPageEvents.length is 0:
    // It means a new filter has reduced eventTotalCount below the limit of the current
    // page. Safest action is to force reset to the first page.
    if (!hasZeroEvents && paginatedEvents.length === 0) {
      setCurrentPage(1);
    }
  }, [hasZeroEvents, paginatedEvents]);

  const headers = [
    {
      name: "",
      className: classNames(css.notableCol),
      cell: {
        className: classNames(css.notableCol),
        component: ({notable}) =>
          notable && (
            <TooltipDelay
              title={t("incidentDetail.detail.importantOnlyTooltip")}
              placement={"right"}>
              <span className={css.importantIconWrapper}>
                <ImportantEventIcon />
              </span>
            </TooltipDelay>
          )
      }
    },
    {
      name: (
        <TooltipDelay title={t("incidents.table.firstSeen")}>
          <div className={"text-truncate"}>
            <p className={css.truncatedContents}>
              {t("incidents.table.firstSeen")}
            </p>
          </div>
        </TooltipDelay>
      ),
      key: FieldNames.observedTime,
      cell: {
        className: classNames(css.firstSeenCol),
        component: (rowData) => (
          <FirstSeenCol event={rowData} handleClick={openDetectionDrawer} />
        )
      },
      sortable: true,
      sort: false
    },
    {
      name: "Severity",
      key: FieldNames.severity,
      sortable: true,
      sort: false,
      cell: {
        className: classNames(css.severityCol),
        component: ({severity}) => <SeverityChip {...{severity}} />
      }
    },
    {
      name: "Source",
      cell: {
        className: classNames(css.sourceCol),
        component: ({source, source_uri}) => (
          <SourceCol {...{source, source_uri, showLink: false}} />
        )
      }
    },
    {
      name: "Indicators",
      cell: {
        className: classNames(css.indicatorsCol),
        component: ({indicators}) => (
          <IndicatorsList
            data={indicators}
            limit={2}
            minimal
            plusMoreHandler={() => {
              openEntityDrawer(indicators, ENTITY_TYPE.indicators);
            }}
          />
        )
      }
    },
    {
      name: "Observables",
      cell: {
        className: classNames(css.observablesCol),
        component: ({observables}) => (
          <ObservablesList
            data={observables}
            limit={2}
            minimal
            plusMoreHandler={() => {
              openEntityDrawer(observables, ENTITY_TYPE.observables);
            }}
          />
        )
      }
    },
    {
      name: "Assets",
      cell: {
        className: classNames(css.assetsCol),
        component: ({targets}) => (
          <AssetsList
            data={targets}
            limit={2}
            minimal
            plusMoreHandler={() => {
              openEntityDrawer(targets, ENTITY_TYPE.assets);
            }}
          />
        )
      }
    }
  ];

  const items = isLoading ? tableLoadingState(eventsPerPage) : paginatedEvents;

  return (
    <div>
      {isEmpty && (
        <TableEmptyState
          entityNamePlural={t("events.title")}
          hasFiltersApplied={hasFiltersApplied}
        />
      )}

      {!isEmpty && (
        <>
          <ADataTable
            data-testid={"EventsTable"}
            tight
            truncateHeaders
            disabled={isLoading || hasZeroEvents}
            headers={headers}
            items={items}
            className={classNames(css.eventsTable, "mx-auto")}
            sort={sort}
            onSort={(s) => setSort(s)}
            disableSortReset
          />
          <div className="d-flex justify-end">
            <APagination
              className="my-2"
              disabled={isLoading}
              onPageChange={setCurrentPage}
              page={currentPage}
              resultsPerPage={eventsPerPage}
              onResultsPerPageChange={setEventsPerPage}
              total={eventTotalCount}
              data-testid={"DetectionTable"}
            />
          </div>
        </>
      )}
    </div>
  );
};

EventsTable.propTypes = {
  /**
   * Total number of events
   */
  eventTotalCount: PropTypes.number,
  /**
   * Number of filters actively applied
   */
  filterCount: PropTypes.number,
  isLoading: PropTypes.bool,
  openDetectionDrawer: PropTypes.func,
  openEntityDrawer: PropTypes.func,
  tableData: PropTypes.array
};

export default EventsTable;
